/**
 * The web mirror of backend `modules/inventory/lot-deduction.ts` (ADR-0047), so the form can say what a
 * save will do before it happens. The server recomputes and is the authority; change both together, and
 * keep the two spec files on the same cases.
 */
import type { InventoryLot, LotContainerType } from '../../services/inventoryService';

const milli = (ml: number): number => Math.round(ml * 1000);
const round3 = (n: number): number => {
  const r = (Math.sign(n) * Math.round(Math.abs(n) * 1000)) / 1000;
  return r === 0 ? 0 : r;
};

/** A volume as containers, on the 3-decimal stock grid. */
export function containersOf(ml: number, mlPerContainer: number): number {
  return round3(ml / mlPerContainer);
}

/** The mL a dose takes: the dose from a multi-dose bottle, whole vials from single-dose ones. */
export function drawMlFor(containerType: LotContainerType, mlPerContainer: number, doseMl: number): number {
  const dose = milli(doseMl);
  if (containerType === 'MULTI_DOSE') return dose / 1000;
  const per = milli(mlPerContainer);
  return (Math.ceil(dose / per) * per) / 1000;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** What saving `count` doses (one per horse on a barn run) does to the lot, in the words the shelf is counted in. */
export function describeDraw(
  lot: Pick<InventoryLot, 'containerType' | 'mlPerContainer' | 'mlRemaining'>,
  doseMl: number,
  count = 1,
): string {
  const draw = round3(drawMlFor(lot.containerType, lot.mlPerContainer, doseMl) * count);
  const given = round3(doseMl * count);
  const after = round3(lot.mlRemaining - draw);
  if (lot.containerType === 'SINGLE_DOSE') {
    const vials = Math.round(draw / lot.mlPerContainer);
    const discarded = round3(draw - given);
    return `Uses ${plural(vials, 'vial', 'vials')}${discarded > 0 ? `, ${discarded} mL discarded` : ''}.`;
  }
  if (after < 0) return `Takes ${given} mL, more than this lot holds (${Math.max(lot.mlRemaining, 0)} mL).`;
  const inOpenBottle = (milli(lot.mlRemaining) % milli(lot.mlPerContainer)) / 1000;
  if (inOpenBottle === 0) {
    const bottles = Math.ceil(milli(draw) / milli(lot.mlPerContainer));
    return bottles === 1 ? `Takes ${given} mL from a new bottle.` : `Takes ${given} mL across ${bottles} new bottles.`;
  }
  return draw <= inOpenBottle
    ? `Takes ${given} mL from the open bottle, leaving ${round3(inOpenBottle - draw)} mL in it.`
    : `Takes ${given} mL, finishing the open bottle and starting a new one.`;
}

type ShelfLot = Pick<InventoryLot, 'containerType' | 'mlPerContainer' | 'mlRemaining'>;

/**
 * Mirror of the backend `shelfCount`: each lot's whole containers and its one open bottle, plus whole containers
 * no lot holds. Null when the parts would not add up to on-hand (a part vial in a single-dose lot included).
 */
export function shelfCount(onHand: number, lots: ReadonlyArray<ShelfLot>): { full: number; openMl: number[] } | null {
  const unassigned = round3(onHand - round3(lots.reduce((sum, lot) => sum + containersOf(lot.mlRemaining, lot.mlPerContainer), 0)));
  if (unassigned < 0 || !Number.isInteger(unassigned) || lots.some((lot) => lot.mlRemaining < 0)) return null;
  let full = unassigned;
  const openMl: number[] = [];
  for (const lot of lots) {
    const ml = milli(lot.mlRemaining);
    const per = milli(lot.mlPerContainer);
    if (ml === 0) continue;
    full += Math.floor(ml / per);
    if (ml % per === 0) continue;
    if (lot.containerType !== 'MULTI_DOSE') return null;
    openMl.push((ml % per) / 1000);
  }
  return { full, openMl };
}

const listMl = (mls: number[]) =>
  mls.length === 1 ? `${mls[0]} mL` : `${mls.slice(0, -1).map((ml) => `${ml} mL`).join(', ')} and ${mls[mls.length - 1]} mL`;

const CONTAINER_WORDS: Record<LotContainerType, [string, string]> = {
  SINGLE_DOSE: ['vial', 'vials'],
  MULTI_DOSE: ['bottle', 'bottles'],
};

/** "9 full bottles + 1 open (8 mL left)" / "8 vials": the shelf in the words it is counted in. Mixed packaging is "containers". */
export function describeShelf(shelf: { containerType: LotContainerType | null; full: number; openMl: number[] }): string {
  const [one, many] = (shelf.containerType && CONTAINER_WORDS[shelf.containerType]) || ['container', 'containers'];
  const open = shelf.openMl.length;
  if (!open) return plural(shelf.full, one, many);
  const left = `(${listMl(shelf.openMl)} left)`;
  if (!shelf.full) return `${open} open ${open === 1 ? one : many} ${left}`;
  return `${shelf.full} full ${shelf.full === 1 ? one : many} + ${open} open ${left}`;
}

/** One lot's balance, the way the shelf is counted; an overdrawn lot reads as empty (its row carries the chip). */
export function describeBalance(lot: ShelfLot): string {
  const held = { ...lot, mlRemaining: Math.max(lot.mlRemaining, 0) };
  const containers = containersOf(held.mlRemaining, held.mlPerContainer);
  const shelf = shelfCount(containers, [held]) ?? { full: Math.floor(containers), openMl: [] };
  return describeShelf({ containerType: lot.containerType, ...shelf });
}
